<template>
  <div>
    <div class="d-flex py-6">
      <v-spacer></v-spacer>
      <v-select
        style="max-width: 250px; margin-right: 24px"
        label="Chọn phòng kiểm tra"
        :items="filteredRooms"
        item-text="name"
        item-value="id"
        v-model="room"
        outlined
        hide-details
        return-object
        dense
      ></v-select>
      <v-btn color="primary" width="118" dark outlined medium class="white--text elevation-0" @click="update"
        >Xếp</v-btn
      >
    </div>
    <v-divider></v-divider>
    <div style="max-height: 35vh; overflow-y: auto">
      <v-data-table
        item-key="id"
        :headers="computedHeaders"
        :loading="loading"
        :items="data.rooms"
        hide-default-footer
        :disable-sort="$vuetify.breakpoint.smAndDown"
        v-bind="this.$attrs"
      >
        <template v-for="subject in subjects" v-slot:[`item.subject_${subject.id}`]="{ item }">
          <div :key="subject.id">
            <v-checkbox :input-value="hasSubject(item, subject.id)" disabled></v-checkbox>
          </div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn color="primary" @click="deleteItem(item)" v-bind="attrs" v-on="on" icon>
                  <v-icon> mdi-close </v-icon>
                </v-btn>
              </template>
              <span>Xếp ra khỏi phòng kiểm tra</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </div>

    <v-divider></v-divider>
    <div class="pt-6" v-if="unassignedSubjectsList !== 'Đã xếp hết'">
      <strong>Môn chưa xếp phòng:</strong> {{ unassignedSubjectsList }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const originHeaders = [
  {
    text: 'Phòng kiểm tra',
    value: 'name',
    align: 'left',
    sortable: false,
    show: true
  },
  {
    text: 'Thao tác',
    value: 'actions',
    align: 'left',
    sortable: false,
    show: true,
    width: '12%'
  }
]

export default {
  props: {
    subjects: Array,
    rooms: Array,
    data: Object
  },
  computed: {
    ...mapGetters('auth', ['user']),
    computedHeaders() {
      const action = this.headers.findIndex(header => header.value === 'actions')
      const before = this.headers.slice(0, action)
      const after = this.headers.slice(action)
      return [...before, ...this.subjectHeaders, ...after]
    },
    filteredRooms() {
      return this.rooms.filter(room => !this.data.rooms.some(dataRoom => dataRoom.id === room.id))
    },
    unassignedSubjectsList() {
      const assignedSubjectIds = new Set()
      this.data.rooms.forEach(room => {
        room.subjects.forEach(subject => {
          assignedSubjectIds.add(subject)
        })
      })

      const unassignedSubjects = this.subjects.filter(subject => !assignedSubjectIds.has(subject.id))

      if (unassignedSubjects.length === 0) {
        return 'Đã xếp hết'
      }

      return unassignedSubjects.map(subject => subject.name).join(', ')
    }
  },
  data() {
    return {
      loading: false,
      headers: originHeaders,
      subjectHeaders: [],
      room: {}
    }
  },
  watch: {
    subjects: {
      handler(newSubjects) {
        this.subjectHeaders = newSubjects.map(subject => ({
          text: subject.name,
          value: `subject_${subject.id}`,
          align: 'left',
          sortable: false,
          width: '10%'
        }))
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('studentExam', ['updateStudentExam', 'fetchStudentExam']),
    hasSubject(room, subjectId) {
      return this.data.data.room[room.id].some(subject => subject === subjectId)
    },
    async update() {
      if (this.room && this.room.id) {
        this.$loading.active = true
        const updatedRooms = [...this.data.rooms, this.room.id]
        if (!this.data.data) {
          this.$set(this.data, 'data', {})
        }
        if (!this.data.data.room) {
          this.$set(this.data.data, 'room', {})
        }
        if (!this.data.data.room[this.room.id]) {
          this.data.data.room[this.room.id] = []
        }
        let allAddedSubjects = new Set()
        Object.values(this.data.data.room).forEach(subjectList => {
          subjectList.forEach(subject => allAddedSubjects.add(subject))
        })

        for (let subject of this.room.subjects) {
          if (!allAddedSubjects.has(subject.id)) {
            this.data.data.room[this.room.id].push(subject.id)
            allAddedSubjects.add(subject.id)
          }
        }
        const status = this.determineStatus(this.data.data)
        const updated = await this.updateStudentExam({
          id: this.data.id,
          rooms: updatedRooms,
          data: this.data.data,
          status
        })
        if (updated) {
          this.getStudentExam()
          this.$emit('refresh')
        } else {
          this.$alert.error('Lỗi')
        }
        this.room = {}
        this.$loading.active = false
      }
    },
    async deleteItem(item) {
      this.$loading.active = true
      this.data.rooms = this.data.rooms.filter(room => room.id !== item.id)

      const subjectsToDelete = this.data.data.room[item.id] || []
      delete this.data.data.room[item.id]

      for (let subjectId of subjectsToDelete) {
        let subjectAdded = false
        for (let roomId in this.data.data.room) {
          if (this.data.rooms.find(r => r.id === roomId).subjects.includes(subjectId)) {
            this.data.data.room[roomId].push(subjectId)
            subjectAdded = true
            break
          }
        }
        if (!subjectAdded) {
          console.warn(`Subject ${subjectId} could not be assigned to any room`)
        }
      }
      const status = this.determineStatus(this.data.data)

      const updated = await this.updateStudentExam({
        id: this.data.id,
        rooms: this.data.rooms.map(room => room.id),
        data: this.data.data,
        status
      })
      if (updated) {
        this.getStudentExam()
        this.$emit('refresh')
      } else {
        this.$alert.error('Error')
      }
      this.$loading.active = false
    },
    async getStudentExam() {
      const studentExam = await this.fetchStudentExam(this.data.id)
      this.data = studentExam
    },
    determineStatus(currentData) {
      const assignedSubjects = new Set()

      for (let roomId in currentData.room) {
        currentData.room[roomId].forEach(subjectId => {
          assignedSubjects.add(subjectId)
        })
      }

      const totalSubjectCount = this.subjects.length
      const assignedSubjectCount = assignedSubjects.size

      if (assignedSubjectCount === 0) {
        return 'notSeated'
      } else if (assignedSubjectCount === totalSubjectCount) {
        return 'seated'
      } else {
        return 'seating'
      }
    },
    close() {
      this.$emit('closeDialog')
    }
  }
}
/* eslint-enable no-unused-vars */
</script>
